import React, { useEffect, useRef } from 'react';

const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let animationFrameId: number;

    // Mouse state
    const mouse = { x: -1000, y: -1000 };

    // Configuration
    // Dust: High density, small size
    const STAR_COUNT = Math.floor((width * height) / 350); 
    // Nodes: The interactive "constellations"
    const NODE_COUNT = Math.floor((width * height) / 15000); 
    const CONNECTION_DIST = 140;
    const MOUSE_DIST = 250;

    interface Star {
      x: number;
      y: number;
      size: number;
      alpha: number;
      twinkleSpeed: number;
      vx: number;
      vy: number;
    }

    interface Node {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
    }

    let stars: Star[] = [];
    let nodes: Node[] = [];
    
    // Shooting star state
    let shootingStar = {
        active: false,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        length: 0
    };

    const init = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;

      // Initialize Background Dust Stars
      stars = [];
      for (let i = 0; i < STAR_COUNT; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          // Slightly larger min size to ensure visibility on all screens
          size: Math.random() * 1.5 + 0.5, 
          alpha: Math.random() * 0.6 + 0.2, // Base opacity
          twinkleSpeed: (Math.random() - 0.5) * 0.005,
          vx: (Math.random() - 0.5) * 0.05, 
          vy: (Math.random() - 0.5) * 0.05
        });
      }

      // Initialize Interactive Nodes
      nodes = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5, // Faster than dust
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1.5,
          color: Math.random() > 0.5 ? '#06b6d4' : '#8b5cf6' // Cyan or Purple
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // 1. Draw Background Dust Stars
      stars.forEach(star => {
        // Twinkle
        star.alpha += star.twinkleSpeed;
        if (star.alpha > 0.8 || star.alpha < 0.2) star.twinkleSpeed *= -1;
        
        // Move
        star.x += star.vx;
        star.y += star.vy;

        // Wrap
        if (star.x < 0) star.x = width;
        if (star.x > width) star.x = 0;
        if (star.y < 0) star.y = height;
        if (star.y > height) star.y = 0;

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`; 
        ctx.fill();
      });

      // 2. Shooting Star
      if (shootingStar.active) {
          shootingStar.x += shootingStar.vx;
          shootingStar.y += shootingStar.vy;
          
          const gradient = ctx.createLinearGradient(
              shootingStar.x, shootingStar.y, 
              shootingStar.x - shootingStar.vx * 15, shootingStar.y - shootingStar.vy * 15
          );
          gradient.addColorStop(0, 'rgba(255,255,255,1)');
          gradient.addColorStop(1, 'rgba(255,255,255,0)');
          
          ctx.beginPath();
          ctx.moveTo(shootingStar.x, shootingStar.y);
          ctx.lineTo(shootingStar.x - shootingStar.vx * 20, shootingStar.y - shootingStar.vy * 20);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.stroke();

          if (shootingStar.x > width + 100 || shootingStar.y > height + 100) {
              shootingStar.active = false;
          }
      } else {
          if (Math.random() < 0.005) { 
              shootingStar.active = true;
              if (Math.random() < 0.5) {
                  shootingStar.x = Math.random() * width;
                  shootingStar.y = -50;
              } else {
                  shootingStar.x = -50;
                  shootingStar.y = Math.random() * height * 0.5;
              }
              const angle = Math.PI / 4 + (Math.random() - 0.5) * 0.2; 
              const speed = Math.random() * 10 + 15;
              shootingStar.vx = Math.cos(angle) * speed;
              shootingStar.vy = Math.sin(angle) * speed;
          }
      }

      // 3. Interactive Nodes & Connections
      nodes.forEach((node, i) => {
        // Move
        node.x += node.vx;
        node.y += node.vy;

        // Bounce
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        // Mouse Attraction
        const dxMouse = mouse.x - node.x;
        const dyMouse = mouse.y - node.y;
        const distMouse = Math.sqrt(dxMouse*dxMouse + dyMouse*dyMouse);
        
        if (distMouse < MOUSE_DIST) {
             node.x += dxMouse * 0.03; // Stronger pull
             node.y += dyMouse * 0.03;
        }

        // Draw Node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        // Subtle glow for nodes
        ctx.shadowBlur = 10;
        ctx.shadowColor = node.color;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Connections between nodes
        for (let j = i + 1; j < nodes.length; j++) {
            const nodeB = nodes[j];
            const dx = node.x - nodeB.x;
            const dy = node.y - nodeB.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < CONNECTION_DIST) {
                ctx.beginPath();
                ctx.moveTo(node.x, node.y);
                ctx.lineTo(nodeB.x, nodeB.y);
                const alpha = 1 - dist / CONNECTION_DIST;
                ctx.strokeStyle = `rgba(100, 200, 255, ${alpha * 0.2})`;
                ctx.lineWidth = 0.8;
                ctx.stroke();
            }
        }

        // Connection to Mouse
        if (distMouse < MOUSE_DIST) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(mouse.x, mouse.y);
            const alpha = 1 - distMouse / MOUSE_DIST;
            ctx.strokeStyle = `rgba(139, 92, 246, ${alpha * 0.5})`; // Brighter purple
            ctx.lineWidth = 1.5; // Thicker line
            ctx.stroke();
        }
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    const handleResize = () => {
        init();
    };

    const handleMouseMove = (e: MouseEvent) => {
        mouse.x = e.clientX;
        mouse.y = e.clientY;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);

    init();
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-[1]" // Z-Index 1 ensures it sits above bg layers
      style={{ background: 'transparent' }} 
    />
  );
};

export default ParticleBackground;